import React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { td } from '@/lib/i18n';
import { z } from 'zod';
import { zodValidator } from '@tanstack/zod-adapter';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus } from 'lucide-react';
import { fetchCampaignAssignments } from '@/services/campaign-assignment';
import { cn } from '@/lib/utils';
const statusSchema = z.enum(['scheduled', 'active', 'ended', 'terminated']);
const searchSchema = z.object({
    status: z.array(statusSchema).default(['scheduled', 'active']),
});
export const Route = createFileRoute('/console/_authenticated/_layout/internal/_layout/assignments/')({
    component: AssignmentsList,
    validateSearch: zodValidator(searchSchema),
});
const STATUS_VARIANT = {
    scheduled: 'secondary',
    active: 'default',
    ended: 'outline',
    terminated: 'destructive',
};
// Lifecycle order: scheduled -> active -> ended/terminated.
const STATUS_FILTERS = [
    {
        value: ['scheduled'],
        statusKey: 'scheduled',
        activeClass: 'bg-blue-500 text-white hover:bg-blue-600 border-transparent',
        inactiveClass: 'border-blue-500/40 text-blue-500 hover:bg-blue-500/10',
    },
    {
        value: ['active'],
        statusKey: 'active',
        activeClass: 'bg-emerald-500 text-white hover:bg-emerald-600 border-transparent',
        inactiveClass: 'border-emerald-500/40 text-emerald-500 hover:bg-emerald-500/10',
    },
    {
        value: ['ended'],
        statusKey: 'ended',
        activeClass: 'bg-slate-500 text-white hover:bg-slate-600 border-transparent',
        inactiveClass: 'border-slate-500/40 text-slate-500 hover:bg-slate-500/10',
    },
    {
        value: ['terminated'],
        statusKey: 'terminated',
        activeClass: 'bg-red-500 text-white hover:bg-red-600 border-transparent',
        inactiveClass: 'border-red-500/40 text-red-500 hover:bg-red-500/10',
    },
];
function AssignmentsList() {
    const { t } = useTranslation('internal');
    const navigate = Route.useNavigate();
    const { status } = Route.useSearch();
    const statusKey = [...status].sort().join(',');
    const { data: allAssignments = [], isLoading } = useQuery({
        queryKey: ['campaign-assignments'],
        queryFn: fetchCampaignAssignments,
    });
    const counts = React.useMemo(() => {
        const c = { scheduled: 0, active: 0, ended: 0, terminated: 0 };
        for (const a of allAssignments) {
            c[a.status]++;
        }
        return c;
    }, [allAssignments]);
    const visibleAssignments = React.useMemo(() => allAssignments.filter((a) => status.includes(a.status)), [allAssignments, status]);
    return (<div className="flex flex-col gap-6 p-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-semibold">{t('assignments.title')}</h1>
                    <p className="text-sm text-muted-foreground">{t('assignments.subtitle')}</p>
                </div>
                <Button asChild>
                    <Link to="/console/internal/assignments/new">
                        <Plus className="h-4 w-4"/>
                        {t('assignments.new')}
                    </Link>
                </Button>
            </div>

            <div className="flex flex-wrap gap-2">
                {STATUS_FILTERS.map((f) => {
            const isActive = [...f.value].sort().join(',') === statusKey;
            return (<Button key={f.statusKey} variant="outline" size="sm" onClick={() => navigate({ search: { status: f.value } })} className={cn('gap-2', isActive ? f.activeClass : f.inactiveClass)}>
                            {td(t, `enum.assignment_status.${f.statusKey}`)}
                            <Badge variant="outline" className={cn('h-5 min-w-5 px-1 text-[10px] tabular-nums', isActive
                    ? 'bg-white/20 text-white border-transparent'
                    : 'border-current bg-transparent')}>
                                {counts[f.statusKey]}
                            </Badge>
                        </Button>);
        })}
            </div>

            {isLoading ? (<p className="text-sm text-muted-foreground">{t('common.loading')}</p>) : visibleAssignments.length === 0 ? (<Card>
                    <CardHeader>
                        <CardTitle>{t('assignments.empty_title')}</CardTitle>
                        <CardDescription>{t('assignments.empty_desc')}</CardDescription>
                    </CardHeader>
                </Card>) : (<div className="grid gap-3 md:grid-cols-2">
                    {visibleAssignments.map((a) => (<Link key={a.publicId} to="/console/internal/assignments/$publicId" params={{ publicId: a.publicId }} className="block">
                            <Card className="h-full hover:border-primary transition-colors">
                                <CardContent className="pt-6">
                                    <div className="flex items-start gap-3">
                                        {a.campaignImageThumbnailUrl && (<img src={a.campaignImageThumbnailUrl} alt="" loading="lazy" className="h-12 w-12 rounded object-cover border shrink-0"/>)}
                                        <div className="flex-1 min-w-0 space-y-2">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <p className="font-semibold truncate">{a.driverName}</p>
                                                    <p className="text-xs text-muted-foreground truncate">
                                                        {a.campaignName}
                                                    </p>
                                                </div>
                                                <Badge variant={STATUS_VARIANT[a.status]} className="shrink-0">
                                                    {td(t, `enum.assignment_status.${a.status}`)}
                                                </Badge>
                                            </div>
                                            <div className="text-xs text-muted-foreground space-y-0.5">
                                                <p>
                                                    {a.startDate.slice(0, 10)} →{' '}
                                                    {a.endDate ? a.endDate.slice(0, 10) : t('assignments.open_to')}
                                                </p>
                                                <p>
                                                    {t('assignments.rate_line', {
                    currency: a.currency,
                    rate: a.monthlyRate,
                    cycle: td(t, `enum.photo_cycle.${a.photoRequestCycle}`),
                })}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </Link>))}
                </div>)}
        </div>);
}
